"use client";

import { Gift, Heart, Truck, Package, Star, Sparkles } from "lucide-react";

export default function GiftShopFeatures() {
  const features = [
    {
      title: "Unique Gifts",
      description: "Carefully curated gifts that stand out and create lasting memories.",
      icon: <Gift size={28} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
    },
    {
      title: "Gift Wrapping",
      description: "Beautiful, complimentary gift wrapping for every special occasion.",
      icon: <Package size={28} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FF8E53 0%, #FFB347 100%)",
    },
    {
      title: "Fast Delivery",
      description: "Express delivery available to ensure your gifts arrive on time.",
      icon: <Truck size={28} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FFB347 0%, #FFC872 100%)",
    },
    {
      title: "Handpicked",
      description: "Every item thoughtfully selected with love and attention to detail.",
      icon: <Heart size={28} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FF6B9D 0%, #FF8EB3 100%)",
    },
    {
      title: "Top Rated",
      description: "Trusted by thousands with 5-star reviews and happy customers.",
      icon: <Star size={28} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FFC872 0%, #FFD89B 100%)",
    },
    {
      title: "Special Touch",
      description: "Personalization options to make your gift truly one-of-a-kind.",
      icon: <Sparkles size={28} strokeWidth={1.8} />,
      gradient: "linear-gradient(135deg, #FF8EB3 0%, #FFB3C6 100%)",
    },
  ];

  const cardStyle: React.CSSProperties = {
    background: "#FFFFFF",
    borderRadius: "24px",
    padding: "32px 24px",
    border: "2px solid #FFF0E6",
    boxShadow: `
      0 2px 4px rgba(255, 255, 255, 0.08),
      0 4px 8px rgba(255,140,83,0.1),
      0 8px 16px rgba(255,140,83,0.12),
      0 16px 32px rgba(63, 63, 63, 0.08)
    `,
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  };

  const responsiveCSS = `
    @media (max-width: 768px) {
      .feature-grid {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 16px !important;
      }
      .feature-card {
        padding: 24px 16px !important;
      }
      .icon-box {
        width: 56px !important;
        height: 56px !important;
        margin-bottom: 12px !important;
      }
      .feature-title {
        font-size: 16px !important;
      }
      .feature-desc {
        font-size: 13px !important;
      }
    }
    @media (min-width: 769px) {
      .feature-grid {
        grid-template-columns: repeat(6, 1fr) !important;
      }
    }
    
    .feature-card::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,182,193,0.15), transparent);
      transition: left 0.6s ease;
    }
    
    .feature-card:hover::before {
      left: 100%;
    }
    
    .feature-card:hover {
      transform: translateY(-8px) scale(1.03);
      box-shadow: 
        0 4px 8px rgba(255,107,107,0.12),
        0 8px 16px rgba(255,140,83,0.15),
        0 16px 32px rgba(255,107,107,0.18),
        0 32px 64px rgba(255,107,107,0.12);
      border-color: #FFD4BA;
    }
    
    .feature-card:hover .icon-box {
      transform: scale(1.15) rotate(5deg);
    }

    @keyframes float {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-10px);
      }
    }

    .gift-emoji {
      animation: float 3s ease-in-out infinite;
    }
  `;

  return (
    <>
      <style>{responsiveCSS}</style>

      <section 
        style={{ 
          background: "linear-gradient(135deg, #ffffffff 0%, #ffffffff 50%, #FFF5EE 100%)",
          padding: "80px 20px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative backgrounds */}
        <div
          style={{
            position: "absolute",
            top: "-80px",
            right: "-120px",
            width: "350px",
            height: "350px",
            background: "radial-gradient(circle, rgba(255,182,193,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-60px",
            left: "-100px",
            width: "300px",
            height: "300px",
            background: "radial-gradient(circle, rgba(255,215,180,0.12) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />

        <div style={{ maxWidth: "1400px", margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
          
          {/* Header */}
          <div style={{ marginBottom: "12px" }}>
            <span className="gift-emoji" style={{ fontSize: "32px", display: "inline-block" }}>🎁</span>
          </div>

          <h2
            style={{
              fontSize: "48px",
              fontWeight: 900,
              color: "#2C2C2C",
              marginBottom: "16px",
              letterSpacing: "-1px",
              lineHeight: 1.2,
            }}
          >
            Why Choose Us For  
            <span 
              style={{ 
                background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
                marginLeft: "8px",
              }}
            >
              Your Gifts
            </span>
          </h2>

          <p
            style={{
              fontSize: "18px",
              color: "#666",
              marginBottom: "50px",
              lineHeight: 1.7,
              maxWidth: "700px",
              marginInline: "auto",
              fontWeight: 500,
            }}
          >
            We make gift-giving special with thoughtfully selected items, beautiful presentation & exceptional service
          </p>

          {/* Feature Cards */}
          <div
            className="feature-grid"
            style={{
              display: "grid",
              gap: "28px",
              gridTemplateColumns: "repeat(6, 1fr)",
            }}
          >
            {features.map((item, index) => (
              <div key={index} className="feature-card" style={cardStyle}>
                <div 
                  className="icon-box" 
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "20px",
                    background: item.gradient,
                    color: "#fff",
                    display: "grid",
                    placeItems: "center",
                    margin: "0 auto 18px auto",
                    boxShadow: `0 4px 16px ${item.gradient.includes('FF6B6B') ? 'rgba(255,107,107,0.3)' : 'rgba(255,140,83,0.25)'}`,
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {item.icon}
                </div>

                <h3 
                  className="feature-title"
                  style={{ 
                    fontSize: "19px", 
                    fontWeight: 800, 
                    marginBottom: "10px", 
                    color: "#2C2C2C",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {item.title}
                </h3>

                <p 
                  className="feature-desc"
                  style={{ 
                    fontSize: "14px", 
                    color: "#666", 
                    lineHeight: 1.6,
                    fontWeight: 500,
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}